import Grid from "@mui/material/Grid/Grid";
import React, {RefObject, useCallback, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useExcessGroupRepository} from "../../../../api/repositories/ExcessGroupRepository";
import {useExcessRepository} from "../../../../api/repositories/ExcessRepository";
import {CarGroup} from "../../../../api/types/carGroups";
import {ExcessResponse, FormExcessAssociationModel} from "../../../../api/types/excess";
import {ExcessGroupAssociationRequest, FormExcessGroupModel} from "../../../../api/types/excessGroup";
import {SelectElementModelFE} from "../../../../api/types/generic";
import CustomBanner from "../../../../common/components/CustomBanner";
import {FormAutocomplete} from "../../../../common/form-fields";
import {excessGroupCreationAtom} from "../../../../state/excessGroupsRecoil";
import {ExcessGroupDetail, ExcessGroupNewDetail} from "../../../../api/types/excessGroupDetail";
import {fromGenericBeToGenericFe, sortCarGroups} from "../../../../utils";
import {BASE_DEFINITION_FORM_STEPS, SET_SNACKBAR} from "../../../../constants";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import {Button, Chip} from "@mui/material";
import {useAcrissRepository} from "../../../../api/repositories/AcrissRepository";

const ExcessGroupAssociation = ({
                                    submitRef,
                                    refresh
                                }: { submitRef: RefObject<HTMLButtonElement>, refresh: Function }) => {
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Form Context
    const {register, setValue, handleSubmit, watch} = useFormContext<FormExcessAssociationModel>();

    // Use Recoil
    const [excessGroupCreation, setExcessGroupCreation] = useRecoilState(excessGroupCreationAtom);
    const {matrixData, readOnly, inUse, completed, groupId} = excessGroupCreation;

    // Use Repository Hooks
    const {getExcesses} = useExcessRepository();
    const {updateExcessMatrix, isExcessGroupInUse} = useExcessGroupRepository();
    const {getOrderedAcriss} = useAcrissRepository();

    const [excesses, setExcesses] = useState<SelectElementModelFE[]>([]);
    const [carGroups, setCarGroups] = useState<SelectElementModelFE[]>([]);

    useEffect(() => {
        getStaticData();
        // eslint-disable-next-line
    }, []);

    const getStaticData = async () => {
        try {
            const excesses = fromGenericBeToGenericFe(await getExcesses());
            // togliere CAR_GROUPS || quando si pusha
            //  const carGroups = CAR_GROUPS || await getOrderedAcriss()
            const carGroupData = await getOrderedAcriss((excessGroupCreation.definitionData as FormExcessGroupModel)?.legalEntity?.code as string);
            const carGroups = carGroupData.map((carGroup) => ({code: carGroup.id, label: carGroup.acriss}));

            setExcesses([{code: 0, label: t("pricing:actions.selectAll")}, ...excesses]);
            setCarGroups([{code: 0, label: t("pricing:actions.selectAll")}, ...carGroups]);
        } catch (e) {
        }
    };

    useEffect(() => {
        if (excesses?.length > 0) {
            const CDR = excesses.find(excess => excess.label === "CDR");
            const TP = excesses.find(excess => excess.label === "TP");
            if (CDR && TP) {
                setValue("excesses", [CDR, TP]);
            }
        }
    }, [excesses]);

    const getInitialValuesFromData = useCallback(() => {
        const init: FormExcessAssociationModel = {carGroups: [], excesses: []};
        const uniqueExcesses: ExcessResponse["id"][] = [];
        const uniqueCarGroups: CarGroup["code"][] = [];
        matrixData?.forEach((detail) => {
            const {carGroup: carGroupCode, excessId} = detail.id;
            if (!uniqueExcesses.includes(excessId)) {
                const excess = excesses!.find((excess) => excess.code === excessId) as SelectElementModelFE;
                uniqueExcesses.push(excessId);
                init.excesses.push(excess);
            }
            if (!uniqueCarGroups.includes(carGroupCode)) {
                const carGroup = carGroups!.find((carGroup) => carGroup.label === carGroupCode) as SelectElementModelFE;
                uniqueCarGroups.push(carGroupCode);
                init.carGroups.push(carGroup);
            }
        });
        setValue("excesses", init.excesses)
        setValue("carGroups", init.carGroups)
    }, [carGroups, excesses, matrixData]);

    useEffect(() => {
        if (excesses?.length && carGroups?.length && matrixData?.length) {
            getInitialValuesFromData();
        }
    }, [getInitialValuesFromData, excesses, carGroups, matrixData]);

    const watchAllForm = watch();

    const confirmAssociation = async (values: FormExcessAssociationModel): Promise<boolean> => {
        const request = createAssociationRequest(values);
        try {
            const response: ExcessGroupNewDetail = await updateExcessMatrix(request);
            const excessGroupIsInUse = await isExcessGroupInUse(groupId as number);
            setExcessGroupCreation({
                ...excessGroupCreation,
                matrixData: response.excessDetails,
                step: BASE_DEFINITION_FORM_STEPS.MATRIX,
                completed: [completed[0], true, completed[2]],
                inUse: excessGroupIsInUse,
                newId: response?.excessGroupId
            });
            refresh()
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            return true;
        } catch (e) {
            return false;
        }
    };

    const createAssociationRequest = ({
                                          carGroups,
                                          excesses
                                      }: FormExcessAssociationModel): ExcessGroupAssociationRequest => {
        const IncomingDetails = sortCarGroups({carGroups}).map<ExcessGroupDetail[]>((carGroup) =>
            excesses.map((excess) => ({
                id: {
                    excessGroupId: excessGroupCreation.groupId || 0,
                    excessId: parseInt(excess.code.toString()),
                    carGroup: carGroup.label
                },
                amount: matrixData?.length
                    ? matrixData?.find(
                    ({id}) => id.carGroup === carGroup.label && id.excessId === parseInt(excess.code.toString())
                )?.amount || 0
                    : 0
            }))
        );

        return {excessGroupId: excessGroupCreation.groupId || 0, incomingDetail: IncomingDetails.flat()};
    };

    const addSelectAllOption = (options: SelectElementModelFE[]) => [
        {code: 0, label: t("pricing:actions.selectAll")},
        ...options
    ];

    const onChangeCarGroups = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.carGroups?.find((el: SelectElementModelFE) => el.code === 0)
                ? // applica il "selectAll
                carGroups.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                input.filter((el) => el.code !== 0);
        setValue("carGroups", value);
    };

    const onChangeExcesses = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.excesses?.find((el: SelectElementModelFE) => el.code === 0)
                ? // applica il "selectAll
                excesses.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo gli excesses selezionati
                input.filter((el) => el.code !== 0);
        setValue("excesses", value);
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {inUse &&
                <CustomBanner message={t("pricing:excessGroup.banner.association")} severity={"warning"}/>}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("carGroups")}
                    label={`${t("pricing:excessGroup.form.labels.carGroup")}*`}
                    handleChange={onChangeCarGroups}
                    options={carGroups}
                    multiple={true}
                    isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                    readOnly={readOnly}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("excesses")}
                    label={`${t("pricing:excessGroup.form.labels.excesses")}*`}
                    options={excesses}
                    isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                    readOnly={readOnly}
                    getOptionDisabled={(option) => ["CDR", "TP"].includes(option.label)}
                    renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                        <Chip
                            label={option.label}
                            {...getTagProps({index})}
                            disabled={["CDR", "TP"].includes(option.label)}
                        />
                    ))}
                    multiple={true}
                    handleChange={onChangeExcesses}
                />
            </Grid>
            <Button ref={submitRef} sx={{display: "none"}} onClick={handleSubmit(confirmAssociation)}/>
        </Grid>
    );
};

export default ExcessGroupAssociation;
